import { Injectable } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { IAppState, User } from '@core/redux/stores';
import { AuthTrackingService, ModalService, CiamFlagsService } from '@core/services';
import {
  MigrationNudgeStateService,
  NudgeDisplayDecision,
  NUDGE_COMPLETION_SOURCE,
  NUDGE_SUPPRESSION_REASON
} from '@core/services/migration-nudge-state.service';
import { JIT_MIGRATION_DISMISSED_ORDER_PREFIX } from '@core/constants/storage-keys';
const PROMPT_MODAL_SELECTOR = 'app-migration-nudge-prompt';
const OTP_MODAL_SELECTOR = 'app-jit-migration-modal';
const HIDDEN_DECISION: NudgeDisplayDecision = { canShow: false, isMandatory: false };

/**
 * Decodes a JWT part (base64url) and returns the parsed object.
 */
function decodeJwtPart(part: string): Record<string, unknown> | null {
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns true if the token is from Auth0.
 * NOTE: This logic is intentionally duplicated in Auth0BearerTokenShape.IsAuth0Shaped (C#).
 * Update both together. See pr-5410-review.md section ANG4.
 */
export function isAuth0Token(token: string | null | undefined): boolean {
  if (!token) return false;
  const parts = token.split('.');

  if (parts.length === 5) {
    const header = decodeJwtPart(parts[0]);
    if (!header) return false;
    const iss = header.iss;
    if (typeof iss === 'string' && iss.toLowerCase().includes('auth0.com')) return true;
    if (header.alg === 'dir') return true;
    return false;
  }

  if (parts.length !== 3) return false;

  const payload = decodeJwtPart(parts[1]);
  const sub = payload?.sub;
  if (typeof sub === 'string' && sub.indexOf('|') >= 0) return true;

  const header = decodeJwtPart(parts[0]);
  if (!header) return false;
  const iss = header.iss;
  if (typeof iss === 'string' && iss.toLowerCase().includes('auth0.com')) return true;

  return false;
}

export function isUserMigratedToAuth0(user: User | null | undefined): boolean {
  const src = user?.idpSource;
  return src === 'Auth0' || src === 'Both';
}

@Injectable()
export class JitMigrationService {
  private lastPlacementSlug: string | null = null;
  private lastOrderId: string | null = null;
  private lastDecision: NudgeDisplayDecision | null = null;
  private readonly postOrderNudgeAttempted = new Set<string>();

  constructor(
    private ngRedux: NgRedux<IAppState>,
    private authTrackingService: AuthTrackingService,
    private modalService: ModalService,
    private ciamFlagsService: CiamFlagsService,
    private nudgeState: MigrationNudgeStateService
  ) {}

  /**
   * Read-only evaluation, used to pick a display delay before committing to showing anything. The
   * authoritative decision is still made inside checkAndShowModal when the timer fires, so a session
   * that changes during the delay self-corrects.
   */
  peekDecision(placementSlug: string): NudgeDisplayDecision {
    if (!this.isEligibleForNudge(placementSlug)) return HIDDEN_DECISION;
    return this.nudgeState.evaluate(this.ciamFlagsService.getNudgeConfig());
  }

  /**
   * Show migration nudge prompt when eligible for the given placement slug.
   * @param placementSlug Statsig nudge_placement value (e.g. post-order, home)
   * @param orderId Required for post-order dismiss tracking
   */
  checkAndShowModal(placementSlug: string, orderId?: string | number): void {
    if (!this.isEligibleForNudge(placementSlug)) return;

    //there is one modal host, so showModal force-closes whatever is already up rather than queueing.
    //Without this the nudge would tear down a blocking modal such as the T&C update and let the user
    //past it. Yielding costs nothing: the next eligible view shows the nudge again
    if (this.modalService.isModalOpen()) {
      //only recorded for a showing that would really have happened, otherwise every cooled-down
      //evaluation that happens to coincide with an open modal inflates the count
      if (this.nudgeState.evaluate(this.ciamFlagsService.getNudgeConfig()).canShow) {
        this.nudgeState.recordSuppressed(placementSlug, NUDGE_SUPPRESSION_REASON.modalBusy);
      }
      return;
    }

    if (placementSlug === 'post-order') {
      const id = orderId != null ? String(orderId) : '';
      if (!id || this.isPostOrderDismissed(id) || this.postOrderNudgeAttempted.has(id)) return;
      this.lastOrderId = id;
      this.postOrderNudgeAttempted.add(id);
    } else if (placementSlug === 'home') {
      //no per-session guard here on purpose: it let a user facing a mandatory nudge tap the CTA, close
      //the OTP modal, and stay free for the rest of the session. The cooldown does the throttling
      this.lastOrderId = null;
    }

    const config = this.ciamFlagsService.getNudgeConfig();
    const decision = this.nudgeState.evaluate(config);
    if (!decision.canShow) {
      if (decision.suppressionReason) {
        this.nudgeState.recordSuppressed(placementSlug, decision.suppressionReason);
      }
      return;
    }

    this.lastPlacementSlug = placementSlug;
    this.lastDecision = decision;
    this.nudgeState.recordShown(config, placementSlug, decision.isMandatory);

    this.modalService.setModalSelector(PROMPT_MODAL_SELECTOR);
    this.modalService.showModal(false);
  }

  openOtpFlowModal(): void {
    this.nudgeState.recordCtaTapped(this.lastPlacementSlug ?? '', this.lastDecision?.isMandatory ?? false);
    this.modalService.setModalSelector(OTP_MODAL_SELECTOR);
    this.modalService.showModal(false);
  }

  getLastPlacementSlug(): string | null {
    return this.lastPlacementSlug;
  }

  getLastOrderId(): string | null {
    return this.lastOrderId;
  }

  getLastDecision(): NudgeDisplayDecision | null {
    return this.lastDecision;
  }

  /**
   * Every way out of the nudge that is not the CTA lands here: the secondary button, the modal close
   * control, and abandoning the OTP flow. All three are real dismissals and advance the same ladder;
   * the trigger tag only says which happened.
   */
  recordPromptDismissed(trigger: string): void {
    const slug = this.lastPlacementSlug ?? '';

    //a mandatory showing spends no dismissal, so walking away from it must not advance the ladder.
    //It is still logged, otherwise a customer who cannot get through the flow is indistinguishable
    //from one who was never blocked. lastDecision is deliberately left in place: the user is still
    //blocked, and reopenMandatoryPrompt needs it
    if (this.lastDecision?.isMandatory) {
      this.nudgeState.recordMandatoryAbandoned(slug);
      return;
    }

    if (slug === 'post-order' && this.lastOrderId) {
      sessionStorage.setItem(JIT_MIGRATION_DISMISSED_ORDER_PREFIX + this.lastOrderId, '1');
    }

    this.nudgeState.recordDismissal(this.ciamFlagsService.getNudgeConfig(), slug, trigger);
    this.lastDecision = null;
  }

  /**
   * Puts the wall back in front of a hard-blocked user who left the OTP flow. Mobile gets this for free
   * because Home re-evaluates every time it appears; here the modal closes without the route changing,
   * so nothing else would bring the nudge back until the next navigation. No-op when the user was never
   * mandatory.
   */
  reopenMandatoryPrompt(): void {
    if (!this.lastDecision?.isMandatory) return;

    const placementSlug = this.lastPlacementSlug ?? '';
    //a full re-check rather than reusing the decision we already hold: the user may have migrated in
    //another tab, and mandatory_enabled may have been switched off while the OTP modal was open, which
    //is the only lever available to release someone who is stuck
    const decision = this.peekDecision(placementSlug);
    if (!decision.isMandatory) {
      this.lastDecision = null;
      return;
    }

    //deferred because the caller is mid-teardown of the OTP modal; showing now would be undone by the
    //hide that follows it
    setTimeout(() => {
      //the usual reason this path runs at all is another modal taking the host, so yield to it rather
      //than close it. The block is not lost, Home puts it back on the next visit
      if (this.modalService.isModalOpen()) {
        this.nudgeState.recordSuppressed(placementSlug, NUDGE_SUPPRESSION_REASON.modalBusy);
        return;
      }

      this.lastDecision = decision;
      this.nudgeState.recordShown(this.ciamFlagsService.getNudgeConfig(), placementSlug, true);
      this.modalService.setModalSelector(PROMPT_MODAL_SELECTOR);
      this.modalService.showModal(false);
    });
  }

  /** @see MigrationNudgeStateService.flushEvents */
  flushEvents(): void {
    this.nudgeState.flushEvents();
  }

  /** Cancelled before it was ever shown, so there is nothing to dismiss. Logged to keep the funnel honest. */
  recordNudgeCancelled(placementSlug: string): void {
    this.nudgeState.recordSuppressed(placementSlug, NUDGE_SUPPRESSION_REASON.cancelled);
  }

  /** Clears the ladder so a migrated user starts clean if they ever land back on a local account. */
  recordMigrationCompleted(): void {
    this.nudgeState.clearState(this.lastPlacementSlug ?? '', NUDGE_COMPLETION_SOURCE.otpSuccess);
    this.lastDecision = null;
    this.lastPlacementSlug = null;
  }

  isEligibleForNudge(placementSlug: string): boolean {
    if (!this.ciamFlagsService.isJitMigration()) return false;
    const config = this.ciamFlagsService.getNudgeConfig();
    if (!config.enabled) return false;
    if (!config.placement.includes(placementSlug)) return false;
    if (!this.authTrackingService.isLoggedIn()) return false;

    const state = this.ngRedux.getState();
    const user = state?.user as User | undefined;
    const token = this.authTrackingService.getAuthToken();
    if (!user || !token) return false;
    if (user.mainAccountType !== 'Local') return false;

    if (isAuth0Token(token) || isUserMigratedToAuth0(user)) {
      //self-heals the reset if the verify callback is ever missed, and is the only way to retest the
      //mandatory flow without clearing site data. clearState is a no-op when there is nothing stored
      this.nudgeState.clearState(placementSlug, NUDGE_COMPLETION_SOURCE.alreadyMigrated);
      return false;
    }

    return true;
  }

  private isPostOrderDismissed(orderId: string): boolean {
    return sessionStorage.getItem(JIT_MIGRATION_DISMISSED_ORDER_PREFIX + orderId) === '1';
  }
}
